import { ref, watch, computed, onUnmounted } from "vue";
import { useMyStore } from "/@/store";
import { postDrc } from "/@/api/drc";
import { UranusMqtt } from "/@/mqtt";
import { ELocalStorageKey } from "/@/types";

// The backend ties DRC control-authority (drc/enter, drc/exit) to a client_id,
// and refuses to release control for any client_id other than the one that
// took it — so a page refresh that mints a brand-new client_id permanently
// strands the old grant until its 1hr Redis TTL expires. Persisting the
// client_id and asking postDrc to renew it (its `client_id` field is
// documented for exactly this) keeps the identity stable across refreshes so
// exit/re-enter keep working against the same held session.
function getStoredClientId(): string | undefined {
	return localStorage.getItem(ELocalStorageKey.DrcClientId) || undefined;
}
function storeClientId(clientId: string) {
	localStorage.setItem(ELocalStorageKey.DrcClientId, clientId);
}

type StatusOptions =
	| {
			status: "close";
			event?: CloseEvent;
	  }
	| {
			status: "open";
			retryCount: number;
	  }
	| {
			status: "pending";
	  }
	| {
			status: "error";
			data?: Error;
	  };

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_BASE_DELAY_MS = 1500;

export function useConnectMqtt() {
	const store = useMyStore();
	const dockOsdVisible = computed(() => {
		return store.state.osdVisible && store.state.osdVisible.visible && store.state.osdVisible.is_dock;
	});

	const droneOsdVisible = computed(() => {
		return store.state.osdVisible && store.state.osdVisible.visible && !store.state.osdVisible.is_dock;
	});
	const mqttState = ref<UranusMqtt | null>(null);

	// Prevents overlapping /drc/connect calls when droneOsdVisible flips
	// rapidly (device switching, double clicks) before a previous connect
	// attempt has resolved — this was the source of the 429s.
	let connecting = false;
	// Bumped on every watcher run / teardown so an in-flight request or a
	// pending retry timer can tell it's stale and bail out.
	let requestToken = 0;
	let retryTimer: ReturnType<typeof setTimeout> | null = null;
	let retryCount = 0;

	function clearRetryTimer() {
		if (retryTimer) {
			clearTimeout(retryTimer);
			retryTimer = null;
		}
	}

	function teardownMqtt() {
		clearRetryTimer();
		if (mqttState.value) {
			mqttState.value.destroyed();
			mqttState.value = null;
			store.commit("SET_MQTT_STATE", null);
			store.commit("SET_CLIENT_ID", "");
		}
	}

	async function connectMqtt(myToken: number) {
		console.log("%c[connectMqtt] ENTRY", "color: magenta; font-weight: bold", {
			myToken,
			requestToken,
			"mqttState.value": mqttState.value,
			"connecting (before check)": connecting,
		});

		if (mqttState.value || connecting) {
			console.log("%c[connectMqtt] EARLY RETURN — guard blocked call", "color: red; font-weight: bold", {
				reason: mqttState.value ? "mqttState.value already truthy" : "connecting already true",
				"mqttState.value": mqttState.value,
				connecting,
			});
			return;
		}

		connecting = true;
		console.log("%c[connectMqtt] connecting = true, proceeding", "color: lime");

		try {
			console.log("%c[connectMqtt] calling postDrc({})...", "color: cyan");
			let result;
			try {
				result = await postDrc({ client_id: getStoredClientId() });
				console.log("%c[connectMqtt] postDrc RESOLVED", "color: lime; font-weight: bold", result);
			} catch (networkError) {
				console.log("%c[connectMqtt] postDrc THREW", "color: red; font-weight: bold", networkError);
				window.console.error("postDrc request threw (network/auth error):", networkError);
				return;
			}

			console.log("%c[connectMqtt] checking staleness", "color: orange", {
				myToken,
				requestToken,
				tokenMismatch: myToken !== requestToken,
				"droneOsdVisible.value": droneOsdVisible.value,
			});

			if (myToken !== requestToken || !droneOsdVisible.value) {
				console.log("%c[connectMqtt] STALE — dropping result", "color: red; font-weight: bold", {
					myToken,
					requestToken,
					"droneOsdVisible.value": droneOsdVisible.value,
				});
				return;
			}

			console.log("%c[connectMqtt] result.code check", "color: orange", { code: result?.code, fullResult: result });

			if (result?.code === 0) {
				console.log("%c[connectMqtt] SUCCESS — building MQTT client", "color: lime; font-weight: bold", result.data);
				const { address, client_id, username, password, expire_time } = result.data;
				storeClientId(client_id);
				const client = new UranusMqtt(address, {
					clientId: client_id,
					username,
					password,
				});
				mqttState.value = client;
				client.initMqtt();
				client.on("onStatus", (statusOptions: StatusOptions) => {
					if (statusOptions.status === "close" || statusOptions.status === "error") {
						handleDisconnect(myToken);
					}
				});

				store.commit("SET_MQTT_STATE", mqttState.value);
				store.commit("SET_CLIENT_ID", client_id);
				retryCount = 0;
				console.log("%c[connectMqtt] MQTT client created + committed to store", "color: lime; font-weight: bold");
			} else {
				console.log("%c[connectMqtt] postDrc returned non-zero code", "color: red; font-weight: bold", result);
				window.console.error("postDrc failed:", result);
			}
		} finally {
			connecting = false;
			console.log("%c[connectMqtt] FINALLY — connecting reset to false", "color: gray");
		}
	}

	function handleDisconnect(myToken: number) {
		// The broker keeps closing the connection right after it opens
		// (auth/session conflict, or the session's just unreachable). Stop
		// the underlying mqtt.js client's own infinite reconnect loop first —
		// this is what was hammering the broker every ~1s indefinitely.
		if (mqttState.value) {
			mqttState.value.destroyed();
			mqttState.value = null;
			store.commit("SET_MQTT_STATE", null);
			store.commit("SET_CLIENT_ID", "");
		}

		// Only retry if the panel is still open and nothing newer has
		// superseded this connection attempt.
		if (myToken !== requestToken || !droneOsdVisible.value) return;

		if (retryCount >= MAX_RECONNECT_ATTEMPTS) {
			window.console.error("mqtt: giving up after", retryCount, "failed reconnect attempts");
			return;
		}

		retryCount += 1;
		const delay = RECONNECT_BASE_DELAY_MS * retryCount; // simple linear backoff
		clearRetryTimer();
		retryTimer = setTimeout(() => {
			if (myToken !== requestToken || !droneOsdVisible.value) return;
			connectMqtt(myToken);
		}, delay);
	}

	watch(
		() => droneOsdVisible.value,
		(val) => {
			console.log("WATCHER is RUNN");
			console.log("%c[useConnectMqtt] WATCHER FIRED, val =", "color: orange; font-weight: bold; font-size: 14px", val);
			const myToken = ++requestToken;
			retryCount = 0;
			clearRetryTimer();

			if (val) {
				connectMqtt(myToken);
				return;
			}

			// 关闭所有小窗后 -> 销毁mqtt连接重置mqtt状态
			teardownMqtt();
		},
		{ immediate: true },
	);

	onUnmounted(() => {
		requestToken++; // invalidate any in-flight attempt/retry
		teardownMqtt();
	});
}
